import { Button, ButtonProps, Portal, Theme, useStyles } from 'bold-ui'
import { useClickOutside } from 'bold-ui/lib/hooks'
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { PopperProps, usePopper } from 'react-popper'

export interface PopperButtonProps extends ButtonProps {
  placement?: PopperProps<any>['placement']
  renderPopper: (controls: PopperControls) => React.ReactNode
  zIndex?: number
  onClose?(): void
}

export interface PopperControls {
  open(): void
  close(): void
  toggle(): void
}

export function PopperButton(props: PopperButtonProps) {
  const { placement, renderPopper, zIndex, onClose, ...buttonProps } = props

  const [isOpen, setOpen] = useState(false)
  const open = useCallback(() => setOpen(true), [])
  const close = useCallback(() => {
    onClose && onClose()
    setOpen(false)
  }, [onClose])
  const toggle = useCallback(() => setOpen((state) => !state), [])

  const { css, classes } = useStyles(createStyles, { isOpen, zIndex })

  const rootRef = useRef<HTMLDivElement>()
  const [buttonRef, setButtonRef] = useState<HTMLElement>()
  const [popperRef, setPopperRef] = useState<HTMLDivElement>()

  useClickOutside(rootRef, () => {
    if (isOpen && onClose) onClose()
    setOpen(false)
  })

  useEffect(() => {
    if (isOpen && popperRef) {
      setTimeout(() => {
        const e: HTMLElement = popperRef.querySelector(
          'a:not([disabled]), button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        e && e.focus()
      })
    }
  }, [isOpen, popperRef])

  const {
    styles: { popper: popperStyle },
  } = usePopper(buttonRef, popperRef, { placement })

  return (
    <>
      <Button innerRef={setButtonRef} style={classes.button} onClick={toggle} {...buttonProps} />

      <Portal>
        <div ref={rootRef}>
          <div
            ref={setPopperRef}
            className={css(classes.popper, popperStyle as any)}
            data-placement={placement}
            data-visible={isOpen}
          >
            {renderPopper({ open, close, toggle })}
          </div>
        </div>
      </Portal>
    </>
  )
}

PopperButton.defaultProps = {
  placement: 'bottom',
} as PopperButtonProps

const createStyles = (theme: Theme, { isOpen, zIndex }) => ({
  popper: {
    zIndex: zIndex ?? theme.zIndex.popper,
    visibility: isOpen ? 'visible' : 'hidden',
    transition: 'opacity .2s ease',
    opacity: isOpen ? 1 : 0,
  } as CSSProperties,
  button: {} as CSSProperties,
})
